/* @jsx h */
import {
  defineComponent,
  h,
  ref,
  computed,
  onMounted,
  onUnmounted,
  watch,
} from "vue";
import { formatTimeInTz } from "@lemmonhq/orders";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import "./GanttChart.css";

dayjs.extend(utc);
dayjs.extend(timezone);

export type Appointment = {
  id: string;
  name: string;
  subtitle?: string;
  type: string;
  start_at: string; // ISO date string
  end_at: string; // ISO date string
  /** Optional domain payload the consumer can attach and read back in the
   *  selection callback (e.g. the reservation behind this appointment). */
  data?: unknown;
};

export type GanttRow = {
  id: string;
  name: string;
  appointments: Appointment[];
};

export type GanttGroup = {
  id: string;
  name: string;
  rows: GanttRow[];
};

export type DeadZone = {
  start: string; // ISO date string
  end: string; // ISO date string
};

export type GanttChartProps = {
  groups: GanttGroup[];
  startTime?: string; // ISO date string, defaults to start of current day
  endTime?: string; // ISO date string, defaults to end of current day
  intervalMinutes?: number; // Default 30 minutes
  columnWidth?: number; // Default 100px
  showCurrentTime?: boolean; // Show current time indicator, defaults to true
  autoScrollToCurrentTime?: boolean; // Auto scroll to current time on load, defaults to true
  currentTimeZone?: string; // IANA timezone for current time indicator
  deadZones?: DeadZone[]; // Closed periods to show with different background
  collapseAll?: boolean; // External control: true = all collapsed, false = all expanded
  onSelect?: (info: {
    groupId: string;
    rowId: string;
    time: string;
    appointment?: Appointment;
  }) => void;
};

export const GanttChart = defineComponent<GanttChartProps>({
  name: "GanttChart",
  props: [
    "groups",
    "startTime",
    "endTime",
    "intervalMinutes",
    "columnWidth",
    "showCurrentTime",
    "autoScrollToCurrentTime",
    "currentTimeZone",
    "deadZones",
    "collapseAll",
    "onSelect",
  ],
  setup(props) {
    // Configuration
    const intervalMinutes = props.intervalMinutes || 30;
    const columnWidth = props.columnWidth || 30;
    const showCurrentTime = props.showCurrentTime !== false;
    const autoScrollToCurrentTime = props.autoScrollToCurrentTime !== false;

    // Refs
    const scrollContainerRef = ref<HTMLElement | null>(null);
    const pinnedColumnRef = ref<HTMLElement | null>(null);
    const pinnedColumnWidth = ref(192);
    const currentTime = ref(new Date());
    const scrollLeft = ref(0);
    const showEasterEgg = ref(false);
    const dispersing = ref(false);
    let clickCount = 0;
    let clickTimer: any = null;

    const handleTimeLineClick = () => {
      if (showEasterEgg.value) return;
      clickCount++;
      if (clickTimer) clearTimeout(clickTimer);
      if (clickCount >= 3) {
        clickCount = 0;
        showEasterEgg.value = true;
        dispersing.value = false;
        // Start dispersion after text is readable
        setTimeout(() => {
          dispersing.value = true;
        }, 3500);
        // Clean up
        setTimeout(() => {
          showEasterEgg.value = false;
          dispersing.value = false;
        }, 5000);
      } else {
        clickTimer = setTimeout(() => {
          clickCount = 0;
        }, 1000);
      }
    };

    const disperseText = (text: string) => {
      if (!dispersing.value) return text;
      return text.split("").map((char, i) => {
        const tx = (Math.random() - 0.5) * 400;
        const ty = (Math.random() - 0.5) * 300;
        const tr = (Math.random() - 0.5) * 180;
        const delay = i * 0.03;
        return h(
          "span",
          {
            class: "char",
            style: `--tx: ${tx}px; --ty: ${ty}px; --tr: ${tr}deg; animation-delay: ${delay}s;`,
          },
          char === " " ? "\u00A0" : char,
        );
      });
    };
    let timeUpdateInterval: ReturnType<typeof setInterval> | null = null;
    let resizeListener: (() => void) | null = null;

    // Collapsed groups state
    const collapsedGroups = ref<Set<string>>(new Set());

    // Toggle group collapse
    const toggleGroupCollapse = (groupId: string) => {
      if (collapsedGroups.value.has(groupId)) {
        collapsedGroups.value.delete(groupId);
      } else {
        collapsedGroups.value.add(groupId);
      }
      // Trigger reactivity
      collapsedGroups.value = new Set(collapsedGroups.value);
    };

    // Watch external collapseAll prop
    watch(
      () => props.collapseAll,
      (val) => {
        if (val === true) {
          collapsedGroups.value = new Set(
            (props.groups || []).map((g) => g.id),
          );
        } else if (val === false) {
          collapsedGroups.value = new Set();
        }
      },
    );

    // Check if group is collapsed
    const isGroupCollapsed = (groupId: string) => {
      return collapsedGroups.value.has(groupId);
    };

    // Time range calculation
    const parseTimeInTimezone = (isoString: string, timezone?: string) => {
      if (!isoString) return new Date();
      const parsed = timezone
        ? dayjs(isoString).tz(timezone)
        : dayjs(isoString);
      return parsed.toDate();
    };

    const timeRange = computed(() => {
      const now = new Date();
      const startOfDay = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
      );
      const endOfDay = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        23,
        59,
        59,
      );

      const start = props.startTime
        ? parseTimeInTimezone(props.startTime, props.currentTimeZone)
        : startOfDay;
      const end = props.endTime
        ? parseTimeInTimezone(props.endTime, props.currentTimeZone)
        : endOfDay;

      return { start, end };
    });

    // Get current time in the specified timezone
    const getCurrentTime = () => {
      return (
        props.currentTimeZone ? dayjs().tz(props.currentTimeZone) : dayjs()
      ).toDate();
    };

    currentTime.value = getCurrentTime();

    watch(
      [() => props.startTime, () => props.endTime, () => props.currentTimeZone],
      () => {
        const { start, end } = timeRange.value;
      },
      { immediate: true },
    );

    // Current time indicator position (inside scroll container)
    const currentTimePosition = computed(() => {
      const { start, end } = timeRange.value;
      const now = currentTime.value;
      const stickyWidth = pinnedColumnWidth.value || 192;

      if (now < start || now > end) return null;

      const offsetMs = now.getTime() - start.getTime();
      const offsetMinutes = offsetMs / (1000 * 60);
      const position = (offsetMinutes / intervalMinutes) * columnWidth;

      return position + stickyWidth;
    });

    watch(currentTimePosition, (pos) => {
      const { start, end } = timeRange.value;
      //      console.log("[Gantt][CurrentTimePosition]", {
      //        position: pos,
      //        now: currentTime.value.toISOString(),
      //        start: start.toISOString(),
      //        end: end.toISOString(),
      //        scrollLeft: scrollLeft.value,
      //        stickyWidth: pinnedColumnWidth.value,
      //        intervalMinutes,
      //        columnWidth,
      //        timezone: props.currentTimeZone,
      //        nowDisplay: formatTimeInTz(currentTime.value.toISOString(), props.currentTimeZone || "UTC"),
      //        startDisplay: formatTimeInTz(start.toISOString(), props.currentTimeZone || "UTC"),
      //        endDisplay: formatTimeInTz(end.toISOString(), props.currentTimeZone || "UTC"),
      //        offsetMinutes: (currentTime.value.getTime() - start.getTime()) / (1000 * 60),
      //      });
    });

    const updatePinnedWidth = () => {
      const width = pinnedColumnRef.value?.getBoundingClientRect().width;
      if (width) {
        pinnedColumnWidth.value = width;
      }
    };

    // Handle scroll event
    const handleScroll = () => {
      if (scrollContainerRef.value) {
        scrollLeft.value = scrollContainerRef.value.scrollLeft;
      }
    };

    // Scroll to current time
    const scrollToCurrentTime = () => {
      if (!scrollContainerRef.value) return;

      const { start, end } = timeRange.value;
      const now = currentTime.value;

      // Check if current time is within the visible range
      if (now < start || now > end) return;

      const offsetMs = now.getTime() - start.getTime();
      const offsetMinutes = offsetMs / (1000 * 60);
      const position = (offsetMinutes / intervalMinutes) * columnWidth;

      // Position the current time about 1 column from the left edge
      const stickyWidth = pinnedColumnWidth.value || 192;
      const scrollPosition = position + stickyWidth - columnWidth;

      scrollContainerRef.value.scrollTo({
        left: Math.max(0, scrollPosition),
        behavior: "smooth",
      });
    };

    // Check if a time column is in a dead zone
    const isInDeadZone = (columnTime: Date) => {
      if (!props.deadZones || props.deadZones.length === 0) return false;

      return props.deadZones.some((zone) => {
        const zoneStart = parseTimeInTimezone(
          zone.start,
          props.currentTimeZone,
        );
        const zoneEnd = parseTimeInTimezone(zone.end, props.currentTimeZone);
        return columnTime >= zoneStart && columnTime < zoneEnd;
      });
    };

    // Setup time update interval, scroll listener, and auto-scroll
    onMounted(() => {
      updatePinnedWidth();

      if (showCurrentTime) {
        timeUpdateInterval = setInterval(() => {
          currentTime.value = getCurrentTime();
        }, 2000);

        // Add scroll listener to track scroll position
        if (scrollContainerRef.value) {
          scrollContainerRef.value.addEventListener("scroll", handleScroll);
        }
      }

      if (autoScrollToCurrentTime) {
        // Small delay to ensure DOM is ready
        setTimeout(() => {
          scrollToCurrentTime();
        }, 0);
      }

      resizeListener = () => updatePinnedWidth();
      window.addEventListener("resize", resizeListener);
    });

    onUnmounted(() => {
      if (timeUpdateInterval) {
        clearInterval(timeUpdateInterval);
      }
      // Remove scroll listener
      if (scrollContainerRef.value) {
        scrollContainerRef.value.removeEventListener("scroll", handleScroll);
      }
      if (resizeListener) {
        window.removeEventListener("resize", resizeListener);
      }
    });

    // Generate time columns
    const timeColumns = computed(() => {
      const { start, end } = timeRange.value;
      const columns = [];
      const current = new Date(start);
      const timeLabelFormatter = props.currentTimeZone
        ? (date: Date) =>
            formatTimeInTz(date.toISOString(), props.currentTimeZone)
        : null;

      while (current < end) {
        columns.push({
          time: new Date(current),
          label: timeLabelFormatter
            ? timeLabelFormatter(current)
            : current.toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              }),
        });
        current.setMinutes(current.getMinutes() + intervalMinutes);
      }

      return columns;
    });

    // Calculate appointment position and width
    const getAppointmentStyle = (appointment: Appointment) => {
      const { start } = timeRange.value;
      const appointmentStart = parseTimeInTimezone(
        appointment.start_at,
        props.currentTimeZone,
      );
      const appointmentEnd = parseTimeInTimezone(
        appointment.end_at,
        props.currentTimeZone,
      );

      const startOffsetMs = appointmentStart.getTime() - start.getTime();
      const durationMs = appointmentEnd.getTime() - appointmentStart.getTime();

      const startOffsetMinutes = startOffsetMs / (1000 * 60);
      const durationMinutes = durationMs / (1000 * 60);

      const leftPosition = (startOffsetMinutes / intervalMinutes) * columnWidth;
      const width = (durationMinutes / intervalMinutes) * columnWidth;

      return {
        left: `${leftPosition}px`,
        width: `${Math.max(width, 20)}px`, // Minimum width of 20px
      };
    };

    // Handle cell click
    const handleCellClick = (
      groupId: string,
      rowId: string,
      timeIndex: number,
      appointment?: Appointment,
    ) => {
      if (props.onSelect) {
        const clickedTime =
          timeColumns.value[timeIndex]?.time || timeRange.value.start;
        props.onSelect({
          groupId,
          rowId,
          time: clickedTime.toISOString(),
          appointment,
        });
      }
    };

    // Get appointment type styling
    const getAppointmentClass = (type: string) => {
      const baseClasses =
        "absolute rounded-md px-2 py-1 flex flex-col justify-center text-[10px] font-medium cursor-pointer transition-all hover:brightness-110 text-white/90";

      switch (type) {
        case "reservation":
          return `${baseClasses} bg-success/20 border border-success/40`;
        case "walk-in":
          return `${baseClasses} bg-info/20 border border-info/40`;
        case "event":
          return `${baseClasses} bg-error/20 border border-error/40`;
        default:
          return `${baseClasses} bg-primary/20 border border-primary/40`;
      }
    };

    return () => (
      <div class="h-full bg-base-100 overflow-hidden relative">
        <div ref={scrollContainerRef} class="h-full overflow-auto">
          <div class="relative">
            {/* Current time indicator - inside scroll area, covers all rows */}
            {showCurrentTime && currentTimePosition.value !== null && (
              <div
                class="current-time-indicator"
                style={{ left: `${currentTimePosition.value}px` }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleTimeLineClick();
                }}
              />
            )}

            {/* Easter egg */}
            {showEasterEgg.value && (
              <div
                class={`easter-egg-message ${dispersing.value ? "dispersing" : ""}`}
              >
                <div class="line1">
                  {dispersing.value ? (
                    disperseText("This Is Now")
                  ) : (
                    <>
                      This Is Now{" "}
                      <span
                        class="material-symbols-outlined"
                        style="font-size: 18px; vertical-align: middle; opacity: 0.5;"
                      >
                        public
                      </span>
                    </>
                  )}
                </div>
                <div class="line2">
                  {dispersing.value ? (
                    disperseText("Compliments, You Are Present")
                  ) : (
                    <>
                      Compliments, You Are Present{" "}
                      <span
                        class="material-symbols-outlined"
                        style="font-size: 14px; vertical-align: middle; opacity: 0.4;"
                      >
                        visibility
                      </span>
                    </>
                  )}
                </div>
                <div class="line3">
                  {dispersing.value ? (
                    disperseText("Be Mindful, Good Luck")
                  ) : (
                    <>
                      Be Mindful, Good Luck{" "}
                      <span
                        class="material-symbols-outlined"
                        style="font-size: 14px; vertical-align: middle; opacity: 0.35;"
                      >
                        sentiment_satisfied
                      </span>
                    </>
                  )}
                </div>
              </div>
            )}

            <table
              class="w-full"
              style={{
                minWidth: `${192 + timeColumns.value.length * columnWidth}px`,
              }}
            >
              <thead>
                <tr class="gantt-pin-rows">
                  <th
                    ref={pinnedColumnRef}
                    class="gantt-pin-cols w-48 px-4 py-3 text-left text-xs font-medium text-base-content/50 uppercase tracking-wider"
                  >
                    <div class="flex items-center gap-2">
                      <span class="material-symbols-outlined text-sm">
                        table_restaurant
                      </span>
                      Tables
                    </div>
                  </th>
                  {timeColumns.value.map((column, index) => {
                    const isDeadZone = isInDeadZone(column.time);
                    return (
                      <th
                        key={index}
                        class={`border-r border-base-300/50 px-2 py-3 text-center text-xs font-medium text-base-content/50 border-b border-base-300/40 ${
                          isDeadZone ? "opacity-30" : ""
                        }`}
                        style={{
                          width: `${columnWidth}px`,
                          minWidth: `${columnWidth}px`,
                        }}
                      >
                        {column.label}
                      </th>
                    );
                  })}
                </tr>
              </thead>

              <tbody>
                {props.groups?.map((group) => (
                  <>
                    {/* Group header */}
                    {group.name && (
                      <tr key={`${group.id}-header`} class="gantt-group-header">
                        <td class="gantt-pin-cols px-3 py-2 text-sm">
                          <div class="flex items-center gap-2">
                            <button
                              onClick={() => toggleGroupCollapse(group.id)}
                              class="hover:bg-base-300/50 rounded p-1 transition-all"
                            >
                              <span
                                class="material-symbols-outlined text-base-content/50 transition-transform duration-200"
                                style={{
                                  transform: isGroupCollapsed(group.id)
                                    ? "rotate(-90deg)"
                                    : "rotate(0deg)",
                                }}
                              >
                                expand_more
                              </span>
                            </button>
                            <span class="material-symbols-outlined text-sm text-primary/50">
                              layers
                            </span>
                            <span class="font-semibold text-base-content/80">
                              {group.name}
                            </span>
                            <span class="text-xs text-base-content/40">
                              ({group.rows.length})
                            </span>
                          </div>
                        </td>
                        {(() => {
                          // Collect all appointments with pixel positions and types
                          const { start } = timeRange.value;
                          const appointments: {
                            leftPx: number;
                            rightPx: number;
                            type: string;
                          }[] = [];

                          group.rows.forEach((row) => {
                            row.appointments?.forEach((appointment) => {
                              const aStart = parseTimeInTimezone(
                                appointment.start_at,
                                props.currentTimeZone,
                              );
                              const aEnd = parseTimeInTimezone(
                                appointment.end_at,
                                props.currentTimeZone,
                              );
                              const startMinutes =
                                (aStart.getTime() - start.getTime()) /
                                (1000 * 60);
                              const endMinutes =
                                (aEnd.getTime() - start.getTime()) /
                                (1000 * 60);
                              const leftPx =
                                (startMinutes / intervalMinutes) * columnWidth;
                              const rightPx =
                                (endMinutes / intervalMinutes) * columnWidth;
                              appointments.push({
                                leftPx,
                                rightPx,
                                type: appointment.type,
                              });
                            });
                          });

                          if (appointments.length === 0) {
                            // No appointments — empty summary td
                            const emptyResult = (
                              <td
                                class="border-b border-base-300/30 relative p-0"
                                colspan={timeColumns.value.length}
                                style={{ height: "56px" }}
                              />
                            );
                            return emptyResult;
                          }

                          // Sweep-line: collect all unique time boundaries
                          const boundaries = new Set<number>();
                          appointments.forEach((a) => {
                            boundaries.add(a.leftPx);
                            boundaries.add(a.rightPx);
                          });
                          const sortedBoundaries = [...boundaries].sort(
                            (a, b) => a - b,
                          );

                          // For each segment between consecutive boundaries, count active appointments
                          const summaryBlocks: {
                            left: number;
                            width: number;
                            confirmed: number;
                            inProgress: number;
                            pending: number;
                          }[] = [];
                          for (
                            let i = 0;
                            i < sortedBoundaries.length - 1;
                            i++
                          ) {
                            const segLeft = sortedBoundaries[i];
                            const segRight = sortedBoundaries[i + 1];
                            if (segRight - segLeft < 1) continue; // skip zero-width

                            let confirmed = 0,
                              inProgress = 0,
                              pending = 0;
                            for (const appt of appointments) {
                              if (
                                appt.leftPx < segRight &&
                                appt.rightPx > segLeft
                              ) {
                                if (appt.type === "reservation") confirmed++;
                                else if (appt.type === "walk-in") inProgress++;
                                else if (appt.type === "event") pending++;
                              }
                            }

                            if (confirmed + inProgress + pending === 0)
                              continue;

                            // Merge with previous block if same counts
                            const prev =
                              summaryBlocks.length > 0
                                ? summaryBlocks[summaryBlocks.length - 1]
                                : null;
                            if (
                              prev &&
                              prev.left + prev.width === segLeft &&
                              prev.confirmed === confirmed &&
                              prev.inProgress === inProgress &&
                              prev.pending === pending
                            ) {
                              prev.width = segRight - prev.left;
                            } else {
                              summaryBlocks.push({
                                left: segLeft,
                                width: segRight - segLeft,
                                confirmed,
                                inProgress,
                                pending,
                              });
                            }
                          }

                          // Build continuous bars per status type — fixed 3 lanes
                          // Lane 0 = in-progress (top), Lane 1 = confirmed (middle), Lane 2 = pending (bottom)
                          const statusConfig = [
                            {
                              key: "inProgress" as const,
                              lane: 0,
                              color: "text-info",
                              bg: "rgba(0,153,227,0.12)",
                              border: "rgba(0,153,227,0.25)",
                            },
                            {
                              key: "confirmed" as const,
                              lane: 1,
                              color: "text-success",
                              bg: "rgba(17,185,129,0.1)",
                              border: "rgba(17,185,129,0.25)",
                            },
                            {
                              key: "pending" as const,
                              lane: 2,
                              color: "text-error",
                              bg: "rgba(202,38,37,0.1)",
                              border: "rgba(202,38,37,0.2)",
                            },
                          ];

                          // For each status type, merge consecutive segments into continuous bars
                          const statusBars: {
                            lane: number;
                            left: number;
                            width: number;
                            count: number;
                            color: string;
                            bg: string;
                            border: string;
                          }[] = [];
                          for (const cfg of statusConfig) {
                            const segments = summaryBlocks.filter(
                              (b) => b[cfg.key] > 0,
                            );
                            if (segments.length === 0) continue;

                            // Merge consecutive segments with same count
                            const merged: {
                              left: number;
                              width: number;
                              count: number;
                            }[] = [];
                            for (const seg of segments) {
                              const prev =
                                merged.length > 0
                                  ? merged[merged.length - 1]
                                  : null;
                              if (
                                prev &&
                                Math.abs(prev.left + prev.width - seg.left) <
                                  1 &&
                                prev.count === seg[cfg.key]
                              ) {
                                prev.width = seg.left + seg.width - prev.left;
                              } else {
                                merged.push({
                                  left: seg.left,
                                  width: seg.width,
                                  count: seg[cfg.key],
                                });
                              }
                            }

                            for (const bar of merged) {
                              statusBars.push({
                                lane: cfg.lane,
                                ...bar,
                                color: cfg.color,
                                bg: cfg.bg,
                                border: cfg.border,
                              });
                            }
                          }

                          const totalLanes = 3;
                          const rowHeight = 56;
                          const laneHeight =
                            Math.floor((rowHeight - 8) / totalLanes) - 1;

                          return (
                            <td
                              class="border-b border-base-300/30 relative p-0"
                              colspan={timeColumns.value.length}
                              style={{ height: `${rowHeight}px` }}
                            >
                              {statusBars.map((bar, i) => (
                                <div
                                  key={i}
                                  class={`absolute flex items-center justify-center rounded-sm text-[11px] font-bold ${bar.color}`}
                                  style={{
                                    left: `${bar.left}px`,
                                    width: `${bar.width}px`,
                                    top: `${4 + bar.lane * (laneHeight + 1)}px`,
                                    height: `${laneHeight}px`,
                                    background: bar.bg,
                                    border: `1px solid ${bar.border}`,
                                    zIndex: 1,
                                  }}
                                >
                                  {bar.count}
                                </div>
                              ))}
                            </td>
                          );
                        })()}
                      </tr>
                    )}

                    {/* Data rows */}
                    {!isGroupCollapsed(group.id) &&
                      group.rows.map((row, rowIndex) => (
                        <tr key={row.id} class="gantt-row group">
                          <td class="gantt-pin-cols h-12 pr-3 pl-12 text-sm">
                            <div class="flex items-center gap-2 text-base-content/60">
                              <span class="material-symbols-outlined text-sm text-base-content/30">
                                table_bar
                              </span>
                              <span>{row.name}</span>
                            </div>
                          </td>
                          {timeColumns.value.map((column, timeIndex) => {
                            // Find appointment that overlaps this cell
                            const overlappingAppointment =
                              row.appointments?.find((appointment) => {
                                const appointmentStart = parseTimeInTimezone(
                                  appointment.start_at,
                                  props.currentTimeZone,
                                );
                                const appointmentEnd = parseTimeInTimezone(
                                  appointment.end_at,
                                  props.currentTimeZone,
                                );
                                const cellTime = column.time;
                                const cellEndTime = new Date(
                                  cellTime.getTime() +
                                    intervalMinutes * 60 * 1000,
                                );

                                // Check if this cell's time range overlaps with the appointment
                                return (
                                  appointmentStart < cellEndTime &&
                                  appointmentEnd > cellTime
                                );
                              });

                            const isDeadZone = isInDeadZone(column.time);

                            return (
                              <td
                                key={timeIndex}
                                class={`time-cell relative h-12 cursor-pointer p-0 ${
                                  isDeadZone ? "opacity-20" : ""
                                }`}
                                style={{
                                  width: `${columnWidth}px`,
                                  minWidth: `${columnWidth}px`,
                                  borderRight:
                                    "1px solid rgba(166,173,187,0.1)",
                                  borderBottom:
                                    "1px solid rgba(166,173,187,0.07)",
                                }}
                                onClick={() =>
                                  handleCellClick(
                                    group.id,
                                    row.id,
                                    timeIndex,
                                    overlappingAppointment,
                                  )
                                }
                              >
                                {/* Appointments */}
                                {row.appointments
                                  ?.filter((appointment) => {
                                    const appointmentStart =
                                      parseTimeInTimezone(
                                        appointment.start_at,
                                        props.currentTimeZone,
                                      );
                                    const appointmentTimeIndex = Math.floor(
                                      (appointmentStart.getTime() -
                                        timeRange.value.start.getTime()) /
                                        (intervalMinutes * 60 * 1000),
                                    );
                                    return appointmentTimeIndex === timeIndex;
                                  })
                                  .map((appointment) => (
                                    <div
                                      key={appointment.id}
                                      class={getAppointmentClass(
                                        appointment.type,
                                      )}
                                      style={{
                                        position: "absolute",
                                        width:
                                          getAppointmentStyle(appointment)
                                            .width,
                                        top: "3px",
                                        bottom: "3px",
                                        left: (() => {
                                          const aStart = parseTimeInTimezone(
                                            appointment.start_at,
                                            props.currentTimeZone,
                                          );
                                          const cellStart =
                                            timeColumns.value[timeIndex].time;
                                          const offsetMs =
                                            aStart.getTime() -
                                            cellStart.getTime();
                                          const offsetPx =
                                            (offsetMs /
                                              (intervalMinutes * 60 * 1000)) *
                                            columnWidth;
                                          return `${Math.max(0, offsetPx)}px`;
                                        })(),
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const appointmentStart =
                                          parseTimeInTimezone(
                                            appointment.start_at,
                                            props.currentTimeZone,
                                          );
                                        const timeIndex = Math.floor(
                                          (appointmentStart.getTime() -
                                            timeRange.value.start.getTime()) /
                                            (intervalMinutes * 60 * 1000),
                                        );
                                        handleCellClick(
                                          group.id,
                                          row.id,
                                          timeIndex,
                                          appointment,
                                        );
                                      }}
                                    >
                                      <span class="truncate font-semibold text-[11px]">
                                        {appointment.name}
                                      </span>
                                      <span class="flex items-center gap-1.5 text-[9px] opacity-70">
                                        <span class="flex items-center gap-0.5">
                                          <span class="material-symbols-outlined text-[10px]">
                                            group
                                          </span>
                                          {appointment.subtitle || ""}
                                        </span>
                                        <span class="flex items-center gap-0.5">
                                          <span class="material-symbols-outlined text-[10px]">
                                            schedule
                                          </span>
                                          {(() => {
                                            const start = parseTimeInTimezone(
                                              appointment.start_at,
                                              props.currentTimeZone,
                                            );
                                            const end = parseTimeInTimezone(
                                              appointment.end_at,
                                              props.currentTimeZone,
                                            );
                                            const mins = Math.round(
                                              (end.getTime() -
                                                start.getTime()) /
                                                60000,
                                            );
                                            return mins < 60
                                              ? `${mins}m`
                                              : `${Math.floor(mins / 60)}h${mins % 60 ? mins % 60 : ""}`;
                                          })()}
                                        </span>
                                      </span>
                                    </div>
                                  ))}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  },
});
